import { BreadcrumbItem, Breadcrumbs } from '@components/Breadcrumbs'
import {
  Box,
  breakpoints,
  CleanButton,
  Flex,
  Icon,
  Text,
  SliderIndicators,
  WishListButton,
  theme
} from '@gassan-ui'
import { useRouter } from 'next/router'
import React, { FC, useState } from 'react'
import { useSwipeable } from 'react-swipeable'
import styled from 'styled-components'
import Slide from './Slide'
import { imgix } from '@lib/imigx'
import { useLocalization } from '@components/Localization'

const Container = styled.section`
  width: 100%;
  position: relative;
  background: ${p => p.theme.colors.shades[50]};
  &::before {
    content: '';
    display: block;
    padding-top: 107.5%;
    @media screen and (min-width: ${breakpoints.small}) {
      padding-top: 74%;
    }
    @media screen and (min-width: ${breakpoints.large}) {
      padding-top: 70%;
    }
  }
`

const Button = styled(CleanButton)<{ direction: string }>`
  position: absolute;
  left: ${p => p.direction === 'prev' && '2.5rem'};
  right: ${p => p.direction === 'next' && '2.5rem'};
  top: 50%;
  transform: translateY(-50%);
  z-index: 5;
  color: ${p => p.theme.colors.shades[500]};
  display: none;
  &:hover {
    color: ${p => p.theme.colors.shades[800]};
  }

  @media screen and (min-width: ${breakpoints.large}) {
    display: block;
  }
`

const ArticleNumber = styled(Text)`
  position: absolute;
  left: 1rem;
  bottom: 0.75rem;
  margin-bottom: 0;

  @media screen and (min-width: ${breakpoints.medium}) {
    left: auto;
    right: 2.5rem;
    bottom: 1.5rem;
  }

  @media screen and (min-width: ${breakpoints.huge}) {
    left: auto;
    right: 2.5rem;
    bottom: 2rem;
  }
`

type Props = {
  id: string
  images: string[]
  title: string
  breadcrumbItems: BreadcrumbItem[]
  variant?: 'default' | 'popover'
  isOnWishList: boolean
  onClickWishlist: () => void
  openFullScreenOnIndex: (index: number) => void
}

const Gallery: FC<Props> = ({
  id,
  images,
  title,
  breadcrumbItems,
  variant = 'default',
  isOnWishList,
  onClickWishlist,
  openFullScreenOnIndex
}) => {
  const { lang } = useLocalization()
  const [index, setIndex] = useState(0)
  const router = useRouter()
  const next = () => (index === images.length - 1 ? setIndex(0) : setIndex(index + 1))
  const prev = () => (index === 0 ? setIndex(images.length - 1) : setIndex(index - 1))
  const handlers = useSwipeable({
    onSwipedLeft: () => next(),
    onSwipedRight: () => prev(),
    preventDefaultTouchmoveEvent: true
  })

  return (
    <Container {...handlers}>
      <Flex
        pt={['1rem', '1.5rem']}
        justifyContent={'space-between'}
        px={['1rem', '2rem', '2rem', '2.5rem']}
        width="100%"
        position="absolute"
        top={0}
        zIndex={5}
      >
        <Breadcrumbs items={breadcrumbItems} />
        <Box display={{ _: 'block', large: 'none' }}>
          {variant === 'popover' && (
            <CleanButton onClick={router.back}>
              <Icon icon="arrow-left" color="shades.800"></Icon>
            </CleanButton>
          )}
        </Box>
        <WishListButton isOnWishlist={isOnWishList} onClick={onClickWishlist}></WishListButton>
      </Flex>
      {images.length > 1 && (
        <>
          <Button direction="prev" onClick={prev}>
            <Icon icon="arrow-left" />
          </Button>
          <Button direction="next" onClick={next}>
            <Icon icon="arrow-right" />
          </Button>
        </>
      )}
      {images.map((src, key) => (
        <Slide
          key={src}
          title={title}
          isActive={key === index}
          placeholderSrc={imgix(src, { width: 50 })}
          onClick={() => openFullScreenOnIndex(key)}
          sources={[
            imgix(src, { width: 400 }),
            imgix(src, { width: 640 }),
            imgix(src, { width: 1000 }),
            imgix(src, { width: 1200 })
          ]}
        />
      ))}
      {images.length > 1 && (
        <Flex
          pb={['.5rem', '1.5rem']}
          justifyContent={['center', 'flex-start']}
          px="2.5rem"
          width="100%"
          position="absolute"
          bottom={['0.25rem', 0]}
          zIndex={5}
        >
          <SliderIndicators
            total={images.length}
            active={index}
            onClickItem={index => setIndex(index)}
          />
        </Flex>
      )}
      <ArticleNumber color={theme.colors.shades[400]} as="span">
        {lang === 'nl' ? `Artnr. ${id}` : `Art. No. ${id}`}
      </ArticleNumber>
    </Container>
  )
}

export default Gallery
